/* eslint-disable sonarjs/cognitive-complexity */
import cx from 'classnames';
import { useEffect, useRef } from 'react';
import { MultiValueProps, OptionProps, ValueContainerProps, components } from 'react-select';
import resetSvg from 'assets/icons/reset.svg';
import Checkbox from 'lib/Checkbox';
import Chip, { Color } from 'lib/Chip';
import { Label } from 'lib/Label';
import AsyncSelect from 'lib/AsyncSelect';
import { SelectDataHandler } from 'lib/AsyncSelect/AsyncSelect.types';
import { DirectSalesStatusOption } from 'components/common/types/DirectSalesCampaign.types';
import usePendoTracking from 'customHooks/usePendoTracking';
import IconButton from 'lib/IconButton';
import { Theme } from 'lib/IconButton/IconButton.types';
import Tooltip, { TooltipDirection } from 'lib/Tooltip';
import { ChipBookingStatus } from '../../Deal/ChipBookingStatus/ChipBookingStatus';
import { UseAllocationReport } from '../useAllocationReport';
import { SelectedLine } from '../AllocationReport.types';

const TruncatedLineName = ({
  name,
  classNames,
  dataTestId = 'truncated-deal-line-name',
}: {
  name: string;
  classNames?: string;
  dataTestId?: string;
}): React.ReactElement => (
  <Tooltip tooltip={name} direction={TooltipDirection.BOTTOM}>
    <p className={cx('truncate', classNames)} data-test-id={dataTestId}>
      {name}
    </p>
  </Tooltip>
);

const SelectedLineLabel = ({
  line,
  isStatusShown,
}: {
  line: SelectedLine;
  isStatusShown: boolean;
}): React.ReactElement =>
  isStatusShown && line.lineStatus ? (
    <div className="flex flex-row items-center gap-x-2 min-w-0">
      <TruncatedLineName name={line.lineName} />
      <div className="shrink-0">
        <ChipBookingStatus status={line.lineStatus} />
      </div>
    </div>
  ) : (
    <TruncatedLineName name={line.lineName} />
  );

type DropdownDealLineProps = Pick<ReturnType<UseAllocationReport>, 'handleChangeLine'> & {
  dealLines: SelectedLine[];
  isLineStatusShown?: boolean;
  isMultiSelectLine?: boolean;
  selectedLines: SelectedLine[];
  resetSelectedLines?: VoidFunction;
  isDisabled?: boolean;
};

/* eslint-disable react/jsx-props-no-spreading */
export const DropdownDealLine: React.FC<DropdownDealLineProps> = ({
  dealLines,
  handleChangeLine,
  selectedLines,
  isLineStatusShown = false,
  isMultiSelectLine = false,
  resetSelectedLines,
  isDisabled = false,
}) => {
  const { getPendoDataId } = usePendoTracking();
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMultiSelectLine) return undefined;

    const handleOutsideMouseDown = (event: MouseEvent): void => {
      const wrapper = wrapperRef.current;
      if (!wrapper || wrapper.contains(event.target as Node)) return;

      const menu = wrapper.querySelector('.react-select__menu');
      if (!menu) return;

      const input = wrapper.querySelector('input');
      if (input) {
        input.focus();
        input.blur();
      }
    };

    document.addEventListener('mousedown', handleOutsideMouseDown);
    return () => document.removeEventListener('mousedown', handleOutsideMouseDown);
  }, [isMultiSelectLine]);

  const handleSearchLines: SelectDataHandler<SelectedLine> = (_, query) => {
    return dealLines.filter(({ lineId, lineName }) => lineName.includes(query) || lineId.includes(query));
  };

  const showSummary = isMultiSelectLine && selectedLines.length > 1;
  const isSingleLineSelectedInMultiMode = isMultiSelectLine && selectedLines.length === 1;

  return (
    <div
      ref={wrapperRef}
      className="flex flex-col w-full md:w-70 z-20"
      data-dd-action-name="Allocation report / Deal line dropdown"
    >
      <Label label="Deal line" />
      <AsyncSelect
        dataTestId="dropdown-deal-lines"
        name="lines"
        textKey="lineName"
        valueKey="lineName"
        defaultOptions={dealLines}
        loadData={handleSearchLines}
        debounceTime={0}
        onSelect={handleChangeLine}
        selectedValue={isMultiSelectLine ? selectedLines : selectedLines[0]}
        isClearable={false}
        isMulti={isMultiSelectLine}
        closeMenuOnSelect={!isMultiSelectLine}
        hideSelectedOptions={false}
        isDisabled={isDisabled}
        dataPendoId={getPendoDataId('Allocation line dropdown')}
        customComponents={{
          ValueContainer: ({ children, ...restProps }: ValueContainerProps<SelectedLine>) => {
            const valueContainerProps = { children, ...restProps } as React.ComponentProps<
              typeof components.ValueContainer
            >;

            if (!showSummary) {
              if (!isSingleLineSelectedInMultiMode) {
                return <components.ValueContainer {...valueContainerProps} />;
              }

              return (
                <components.ValueContainer {...valueContainerProps}>
                  <div className="flex flex-nowrap items-center w-full min-w-0" data-test-id="value-container-no-wrap">
                    {children}
                  </div>
                </components.ValueContainer>
              );
            }

            return (
              <components.ValueContainer {...valueContainerProps}>
                <div className="flex items-center w-full body-base">
                  <p className="whitespace-nowrap">Multiple deal lines selected</p>

                  <div
                    className="flex pl-2 flex-shrink-0"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                    role="presentation"
                  >
                    <Chip label={selectedLines.length.toString()} color={Color.GREY} />

                    <div className="inline-block h-full w-auto relative">
                      <IconButton icon={resetSvg} theme={Theme.PLAIN} handleOnClick={resetSelectedLines} name="reset" />
                    </div>
                  </div>
                  {children}
                </div>
              </components.ValueContainer>
            );
          },
          MultiValue: ({ data }: MultiValueProps<SelectedLine>) => {
            if (showSummary) {
              return null;
            }

            return (
              <div className="body-base min-w-0">
                <SelectedLineLabel line={data} isStatusShown={isLineStatusShown} />
              </div>
            );
          },
          MultiValueRemove: () => null,
          SingleValue: ({ data, ...props }: OptionProps & { data: SelectedLine }) => {
            return (
              <components.SingleValue data={data} {...props}>
                <SelectedLineLabel line={data} isStatusShown={isLineStatusShown} />
              </components.SingleValue>
            );
          },
          Option: ({ data, ...props }: OptionProps & { data: SelectedLine }) => {
            const isOptionDisabled = isMultiSelectLine && data.lineStatus === DirectSalesStatusOption.CANCELLED;

            return (
              <components.Option
                data={data}
                {...props}
                isDisabled={isOptionDisabled}
                innerProps={{
                  ...props.innerProps,
                  'aria-disabled': isOptionDisabled,
                  onClick: isOptionDisabled ? undefined : props.innerProps.onClick,
                }}
              >
                <div
                  className={cx('flex justify-between space-x-2', {
                    'opacity-50 cursor-not-allowed': isOptionDisabled,
                  })}
                >
                  {isMultiSelectLine && (
                    <div className="mt-[0.25rem] shrink-0">
                      <Checkbox isSelected={!!selectedLines?.find(({ lineId }) => data.lineId === lineId)} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0 text-left" data-test-id="option-deal-info">
                    <TruncatedLineName
                      name={data.lineName}
                      classNames="body-lg text-neutral-950"
                      dataTestId="option-deal-info-name"
                    />
                    <p className="body-base text-neutral-600 truncate" data-test-id="option-deal-info-id">
                      {data.lineId}
                    </p>
                  </div>
                  {isLineStatusShown && data.lineStatus && (
                    <div className="shrink-0">
                      <ChipBookingStatus status={data.lineStatus} />
                    </div>
                  )}
                </div>
              </components.Option>
            );
          },
          Placeholder: ({ data, ...props }: OptionProps & { data: SelectedLine }) => {
            return (
              <components.Placeholder {...props}>
                <div className="flex justify-between items-center w-full">
                  <span>Select deal line(s)</span>

                  {isMultiSelectLine && (
                    <div className="inline-block h-full w-auto relative">
                      <IconButton icon={resetSvg} theme={Theme.PLAIN} handleOnClick={resetSelectedLines} name="reset" />
                    </div>
                  )}
                </div>
              </components.Placeholder>
            );
          },
        }}
      />
    </div>
  );
};
/* eslint-enable sonarjs/cognitive-complexity */
/* eslint-enable react/jsx-props-no-spreading */
